// Spectator HUD (§9.6, §14.2): "SPECTATING — Grumbo" with ◀ ▶ to cycle the followed bean (←/→,
// A/D, Q/E, D-pad, LB/RB, or tap the arrows), the seat-queue position for a room full of humans
// ("You're next for a seat"), and for a ghost who chose Spectate a way back to pebble duty. It
// is an overlay: the in-round HUD stays live underneath.
import { NO_SLOT } from '@shared/constants';
import type { LiveScreen, NavAction } from '../contracts';
import type { UiDeps } from '../deps';
import { actionCap } from '../glyphView';
import { identityBadge } from '../icons';
import { ordinal } from '../labels';
import { el, replayClass, setHidden, setText } from '../ui';
import { button } from '../widgets';

const CYCLE_KEYS: Readonly<Record<string, 1 | -1>> = { ArrowLeft: -1, ArrowRight: 1, KeyA: -1, KeyD: 1, KeyQ: -1, KeyE: 1 };

export function create(deps: UiDeps): LiveScreen {
  const badge = el('span', { class: 'spec-badge' });
  const name = el('span', { class: 'spec-name ellipsis' });
  const queue = el('div', { class: 'spec-queue' });
  const prev = button({ icon: 'arrowL', title: 'Previous bean', variant: 'cream', size: 'small', cls: 'round', onClick: () => cycle(-1) });
  const next = button({ icon: 'arrowR', title: 'Next bean', variant: 'cream', size: 'small', cls: 'round', onClick: () => cycle(1) });
  const back = button({ label: 'BACK TO PEBBLES', icon: 'ghost', variant: 'grape', size: 'small', onClick: () => deps.actions.spectate(false) });
  const hintL = el('span', { class: 'spec-hint' });
  const hintR = el('span', { class: 'spec-hint' });
  const bar = el('div', { class: 'panel spec-bar' },
    el('div', { class: 'spec-label', text: 'SPECTATING' }),
    el('div', { class: 'spec-row' }, hintL, prev, el('div', { class: 'spec-who' }, badge, name), next, hintR),
    queue, back);
  const root = el('div', { class: 'screen overlay spectator', attrs: { 'data-nofocus': true } }, bar);
  let focusKey = '';

  function cycle(dir: 1 | -1): void {
    deps.actions.cycleFocus(dir);
    deps.services.playUi('hover');
    replayClass(bar, dir > 0 ? 'nudge-r' : 'nudge-l');
  }

  const onKey = (e: KeyboardEvent): void => {
    if (e.repeat || root.hidden) return;
    const t = e.target;
    if (t instanceof HTMLInputElement) return;
    const dir = CYCLE_KEYS[e.code];
    if (dir) cycle(dir);
  };

  function renderHints(): void {
    const dev = deps.services.input.device;
    hintL.replaceChildren(...(dev === 'pad' ? [actionCap(deps, 'tabL')] : dev === 'kbm' ? [el('span', { class: 'key', text: '←' })] : []));
    hintR.replaceChildren(...(dev === 'pad' ? [actionCap(deps, 'tabR')] : dev === 'kbm' ? [el('span', { class: 'key', text: '→' })] : []));
  }
  deps.services.input.onChange(() => {
    if (!root.hidden) renderHints();
  });

  return {
    el: root,
    onShow() {
      focusKey = '';
      renderHints();
      window.addEventListener('keydown', onKey);
    },
    onHide() {
      window.removeEventListener('keydown', onKey);
    },
    onNav(action: NavAction) {
      if (action === 'left' || action === 'tabL') {
        cycle(-1);
        return true;
      }
      if (action === 'right' || action === 'tabR') {
        cycle(1);
        return true;
      }
      return false;
    },
    update(view) {
      const slot = view.focusSlot;
      const b = slot !== NO_SLOT ? view.beans[slot] : null;
      const key = b ? `${slot}:${b.name}:${b.identity}` : '-';
      if (key !== focusKey) {
        focusKey = key;
        badge.replaceChildren(...(b ? [identityBadge(b.identity, deps.settings().accessibility.colorblind, 'idbadge')] : []));
        setText(name, b ? b.name || `Bean ${slot + 1}` : 'the arena');
        replayClass(name, 'swap');
      }
      const room = deps.room;
      const seat = room.spectator;
      setHidden(back, seat);
      if (seat && room.dto) {
        const me = deps.profile().name;
        const i = room.dto.spectators.findIndex((s) => s.name === me);
        setText(queue, i <= 0 ? "You're next for a seat — it opens at the next intermission" : `${ordinal(i + 1)} in line for a seat`);
        setHidden(queue, false);
      } else {
        // §9.6: ghost control is released while spectating (no pebbles, honks or emotes).
        setText(queue, "You're out this round — follow the action, or go back to dropping pebbles");
        setHidden(queue, false);
      }
    },
  };
}
